import {useEffect, useRef} from 'react';
import {CONTEST_ACTIONS} from '@/hooks/contest/contestActions.js';
import {EVENT_TOPIC} from '@/api/endpoints.js';
import {mapBackendToFrontend} from '@/utils/mappers.js';
import {getParticipants, getMyState} from '@/api/participant.js';
import {APP_STEPS} from '@/hooks/contest/contestSteps.js';
import {createContestStream} from "@/features/contest/services/streamService.js";
import {CONTEST_STATUS, PARTICIPANT_STATUS} from "@/hooks/contest/contestStatus.js";
import {syncUserContestStatus} from "@/features/contest/services/contestService.js";

const WATCHDOG_MS = 50000; // pas de message (event OU heartbeat) depuis 50s → connexion présumée morte

export const useContestLive = (contestId, dispatch, state) => {

    const listLengthRef = useRef(state.leaderboard.length);
    const scoreRef = useRef(state.matchScore);
    const matchDateRef = useRef(state.contestData?.fixture?.startingAt);
    const userInfoRef = useRef(state.userInfo);
    const stepRef = useRef(state.step);

    const esRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const watchdogRef = useRef(null);
    const hasConnectedOnceRef = useRef(false); // 1ère connexion = état déjà chargé → pas de réconciliation

    // --- SYNCHRONISATION DES REFS ---
    useEffect(() => {
        scoreRef.current = state.matchScore;
        listLengthRef.current = state.leaderboard.length;
        userInfoRef.current = state.userInfo;
        stepRef.current = state.step;
        matchDateRef.current = state.contestData?.fixture?.startingAt;
    }, [state.leaderboard.length, state.contestData?.fixture?.startingAt, state.matchScore, state.userInfo, state.step]);


    // --- LE SSE (Stable) ---
    useEffect(() => {
        if (!contestId) return;

        // Watchdog anti-zombie : tant qu'on reçoit des messages (event OU heartbeat), on est vivant.
        // Au-delà de WATCHDOG_MS sans rien, on présume la connexion morte (half-open) et on force la
        // reconnexion — sans faire confiance à readyState (qui peut rester OPEN sur une connexion morte).
        const resetWatchdog = () => {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            watchdogRef.current = setTimeout(() => {
                console.warn("🐶 Watchdog: aucun message depuis 50s → connexion présumée morte, reconnexion forcée.");
                if (esRef.current) {
                    esRef.current.close();
                    esRef.current = null; // force connect() à repartir (sinon le garde readyState le bloque)
                }
                connect();
            }, WATCHDOG_MS);
        };

        const connect = () => {
            // Sécurité : Si une connexion est déjà active/ouverte, on évite de la tuer pour rien
            if (esRef.current && esRef.current.readyState === EventSource.OPEN) {
                console.log("🟢 Live: Connexion déjà active, reconnexion ignorée.");
                return;
            }

            if (esRef.current) {
                esRef.current.close();
            }

            console.log("🔌 Live: Connexion au flux...");
            const es = createContestStream(contestId);
            esRef.current = es;

            es.onopen = () => {
                console.log("✅ Live: Flux connecté !");
                resetWatchdog();
                // Réconciliation : à CHAQUE reconnexion (pas la 1ère), on rattrape les events manqués
                // pendant la coupure (classement, lot mi-temps, fin de match).
                if (hasConnectedOnceRef.current) {
                    reconcile(contestId, dispatch);
                } else {
                    hasConnectedOnceRef.current = true;
                }
            };

            es.addEventListener(EVENT_TOPIC.HEARTBEAT, () => {
                resetWatchdog();
            });

            es.addEventListener(EVENT_TOPIC.CONTEST_UPDATE, (e) => {
                resetWatchdog();
                const update = JSON.parse(e.data);

                if (update?.status === CONTEST_STATUS.COMPLETED) {
                    console.log("🏁 Live: Match terminé, fermeture du flux.");
                    es.close();
                    syncUserContestStatus(update.contestId, update.status, dispatch)
                        .catch(err => console.error("Erreur sync status", err));
                    return;
                }

                // Pas de fixture → classement modifié hors match (ex: bonus quiz appliqué,
                // distribution des lots mi-temps) → on recharge le classement + l'état perso
                // (sans navigation) pour détecter un éventuel lot mi-temps gagné.
                if (!update.fixture) {
                    reloadFullList(contestId, dispatch);
                    refreshHalfTimeReward(contestId, dispatch);
                    return;
                }

                // Le score live vit dans update.fixture (homeScore/awayScore), pas au niveau racine.
                const newScore = {team1: update.fixture.homeScore, team2: update.fixture.awayScore};
                const oldScore = scoreRef.current;

                const scoreChanged =
                    (newScore.team1 ?? 0) !== (oldScore.team1 ?? 0) ||
                    (newScore.team2 ?? 0) !== (oldScore.team2 ?? 0);

                if (scoreChanged) {
                    dispatch({type: CONTEST_ACTIONS.SET_GOAL_OVERLAY, payload: true});
                    setTimeout(() => {
                        dispatch({type: CONTEST_ACTIONS.SET_GOAL_OVERLAY, payload: false});
                    }, 3000);
                }

                dispatch({type: CONTEST_ACTIONS.LIVE_MATCH_UPDATE, payload: update});
            });

            es.addEventListener(EVENT_TOPIC.PARTICIPANT_JOINED, (e) => {
                resetWatchdog();
                const payload = JSON.parse(e.data);
                const {totalParticipants, ...rawPlayer} = payload;
                const [formattedPlayer] = mapBackendToFrontend([rawPlayer]);

                const expectedLength = listLengthRef.current + 1;
                const isDesynced = totalParticipants !== expectedLength;

                if (isDesynced) {
                    const myPseudo = userInfoRef.current?.pseudo;
                    const isMeJoining = stepRef.current === APP_STEPS.FORM && formattedPlayer.pseudo === myPseudo;

                    if (isMeJoining) {
                        console.log("🙈 Live: C'est moi, on ignore la désynchro.");
                    } else {
                        console.warn("⚠️ Live: Désynchro détectée. Rechargement complet.");
                        reloadFullList(contestId, dispatch);
                        return;
                    }
                }

                dispatch({
                    type: CONTEST_ACTIONS.LIVE_PARTICIPANT_JOINED,
                    payload: formattedPlayer
                });
            });

            es.onerror = () => {
                if (esRef.current?.readyState === EventSource.CLOSED) {
                    setTimeout(connect, 2000);
                }
            };
        };

        connect();

        // --- GESTION DU RÉVEIL MOBILE ---
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log("📱 Réveil : Vérification de la connexion...");

                // Nettoyage d'un éventuel timeout précédent
                if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

                reconnectTimeoutRef.current = setTimeout(() => {
                    if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) {
                        // Flux fermé → on relance (onopen réconciliera).
                        console.log("📱 Réveil : Connexion perdue, on relance.");
                        connect();
                    } else {
                        // Flux "ouvert" mais potentiellement zombie après la veille : on ne se fie pas
                        // à readyState, on réconcilie immédiatement + on réarme le watchdog.
                        console.log("📱 Réveil : Connexion active, réconciliation de sécurité.");
                        reconcile(contestId, dispatch);
                        resetWatchdog();
                    }
                }, 500);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // CLEANUP
        return () => {
            console.log("🧹 Live: Cleanup (Démontage ou changement ID)");

            // 1. Annuler tout timeout de reconnexion / watchdog en cours (CRUCIAL)
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
            if (watchdogRef.current) clearTimeout(watchdogRef.current);

            // 2. Fermer le flux
            if (esRef.current) {
                esRef.current.close();
                esRef.current = null;
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };

    }, [contestId, dispatch]);
};

// Réconciliation à la reconnexion / au réveil : rattrape ce qui a été manqué pendant la coupure,
// SANS éjecter le joueur de son étape en cours (on ne route que si le match est terminé).
async function reconcile(contestId, dispatch) {
    // 1. Classement (rangs/points : buts, bonus, scoring mi-temps appliqués pendant la coupure)
    await reloadFullList(contestId, dispatch);

    // 2. État perso : lot mi-temps gagné + détection d'un match terminé pendant la coupure
    try {
        const {data: myState, error} = await getMyState(contestId);
        if (myState && !error) {
            dispatch({type: CONTEST_ACTIONS.SET_HALF_TIME_REWARD, payload: myState.halfTimeReward ?? null});
            if (myState.reward) {
                dispatch({type: CONTEST_ACTIONS.SET_USER_REWARD, payload: myState.reward});
            }
            // Le match s'est terminé pendant qu'on était déconnecté → on route vers le résultat.
            if (myState.status === PARTICIPANT_STATUS.WON) {
                dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.RESULT_WIN});
            } else if (myState.status === PARTICIPANT_STATUS.LOST) {
                dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.RESULT_LOSE});
            }
        }
    } catch (e) {
        console.error("Erreur réconciliation SSE", e);
    }
}

async function reloadFullList(contestId, dispatch) {
    try {
        const rawList = await getParticipants(contestId);
        const formatted = mapBackendToFrontend(rawList);
        dispatch({
            type: CONTEST_ACTIONS.UPDATE_LEADERBOARD,
            payload: {list: formatted}
        });
    } catch (e) {
        console.error("Erreur reload live", e);
    }
}

// Recharge uniquement le lot mi-temps (pas de navigation/step) : le joueur reste sur le classement.
async function refreshHalfTimeReward(contestId, dispatch) {
    try {
        const {data: myState, error} = await getMyState(contestId);
        if (myState && !error) {
            dispatch({type: CONTEST_ACTIONS.SET_HALF_TIME_REWARD, payload: myState.halfTimeReward ?? null});
        }
    } catch (e) {
        console.error("Erreur refresh lot mi-temps", e);
    }
}